export interface AbilityUnlockRule {
  abilityId: string;
  speciesId: string;
  count: number;
}

export type AbilityListener = (abilityId: string) => void;

export interface AbilitySystemOptions {
  rules?: AbilityUnlockRule[];
  techPointsPerKill?: number;
}

export class AbilitySystem {
  private readonly counts = new Map<string, number>();
  private readonly unlocked = new Set<string>();
  private readonly rules: AbilityUnlockRule[];
  private readonly techPointsPerKill: number;
  private listeners: AbilityListener[] = [];
  private techPoints = 0;

  constructor(options: AbilitySystemOptions = {}) {
    this.rules = options.rules ?? [];
    this.techPointsPerKill = options.techPointsPerKill ?? 1;
  }

  recordKill(speciesId: string): void {
    const next = (this.counts.get(speciesId) ?? 0) + 1;
    this.counts.set(speciesId, next);
    this.techPoints += this.techPointsPerKill;
    this.checkUnlocks();
  }

  /** Direct state replace — used by save-restore. Does NOT emit for re-established unlocks. */
  setState(input: {
    counts: Record<string, number>;
    unlocked: readonly string[];
    techPoints: number;
  }): void {
    this.counts.clear();
    for (const [k, v] of Object.entries(input.counts)) {
      this.counts.set(k, v);
    }
    this.unlocked.clear();
    for (const id of input.unlocked) this.unlocked.add(id);
    this.techPoints = input.techPoints;
  }

  hasAbility(abilityId: string): boolean {
    return this.unlocked.has(abilityId);
  }

  getCount(speciesId: string): number {
    return this.counts.get(speciesId) ?? 0;
  }

  allUnlocked(): readonly string[] {
    return Array.from(this.unlocked);
  }

  allCounts(): Readonly<Record<string, number>> {
    return Object.fromEntries(this.counts);
  }

  get techPointsAvailable(): number {
    return this.techPoints;
  }

  addRule(rule: AbilityUnlockRule): void {
    this.rules.push(rule);
    this.checkUnlocks();
  }

  onUnlock(listener: AbilityListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private checkUnlocks(): void {
    for (const rule of this.rules) {
      if (this.unlocked.has(rule.abilityId)) continue;
      if ((this.counts.get(rule.speciesId) ?? 0) >= rule.count) {
        this.unlocked.add(rule.abilityId);
        this.emit(rule.abilityId);
      }
    }
  }

  private emit(abilityId: string): void {
    for (const listener of this.listeners) {
      listener(abilityId);
    }
  }
}
